import type { Estimate, EstimateLine, OutboxItem } from '@fruitways/shared';
import { db } from './database';
import { drainOutbox, enqueue } from './outbox';
import type { Draft } from '@/capture/CaptureContext';

export type SubmitOutcome = 'submitted' | 'saved' | 'updated' | 'deleted';

function linesFrom(draft: Draft): EstimateLine[] {
  return draft.picked.map((code) => ({
    varietyCode: code,
    bins: [...(draft.values[code] ?? [0, 0, 0, 0, 0, 0])] as EstimateLine['bins'],
  }));
}

function nowIso(): string {
  return new Date().toISOString();
}

/** A pending create for this estimate still on the phone, if any. */
async function pendingCreate(estimateId: string) {
  const items = await db.outbox.where('estimateId').equals(estimateId).toArray();
  return items.find((i): i is Extract<OutboxItem, { kind: 'create' }> => i.kind === 'create');
}

/**
 * Save the draft to the phone first, queue the send, then try to send right away.
 * Resolves with what the success screen should say.
 */
export async function submitDraft(
  draft: Draft,
  userId: string,
): Promise<{ outcome: SubmitOutcome; estimate: Estimate }> {
  if (!draft.farmId || !draft.seasonId) throw new Error('Draft incomplete');
  const lines = linesFrom(draft);
  const capturedAt = nowIso();

  if (draft.editingId) {
    const id = draft.editingId;
    const existing = await db.estimates.get(id);
    if (!existing) throw new Error('Estimate missing');
    const pending = await pendingCreate(id);
    const local: Estimate = {
      ...existing,
      lines,
      status: pending ? 'queued' : 'edited',
      updatedAt: capturedAt,
    };
    await db.transaction('rw', db.estimates, db.outbox, async () => {
      await db.estimates.put(local);
      if (pending) {
        // Never sent yet: fold the change into the queued create.
        const merged: Extract<OutboxItem, { kind: 'create' }> & { seq?: number } = {
          ...pending,
          payload: { ...pending.payload, lines, capturedAt },
        };
        await db.outbox.put(merged);
      } else {
        await enqueue({
          id: crypto.randomUUID(),
          kind: 'update',
          estimateId: id,
          payload: { lines, capturedAt },
          createdAt: capturedAt,
          attempts: 0,
        });
      }
    });
    await drainOutbox();
    const after = (await db.estimates.get(id)) ?? local;
    return { outcome: 'updated', estimate: after };
  }

  const id = crypto.randomUUID();
  const local: Estimate = {
    id,
    farmId: draft.farmId,
    seasonId: draft.seasonId,
    weekNumber: draft.weekNumber,
    weekStart: draft.weekStart,
    status: 'queued',
    lines,
    submittedAt: capturedAt,
    submittedBy: userId,
    updatedAt: capturedAt,
  };
  await db.transaction('rw', db.estimates, db.outbox, async () => {
    await db.estimates.put(local);
    await enqueue({
      id: crypto.randomUUID(),
      kind: 'create',
      estimateId: id,
      payload: {
        id,
        farmId: local.farmId,
        seasonId: local.seasonId,
        weekNumber: local.weekNumber,
        weekStart: local.weekStart,
        lines,
        capturedAt,
      },
      createdAt: capturedAt,
      attempts: 0,
    });
  });
  await drainOutbox();
  const after = (await db.estimates.get(id)) ?? local;
  const stillQueued = Boolean(await pendingCreate(id));
  return { outcome: stillQueued ? 'saved' : 'submitted', estimate: after };
}

/** Remove locally, queue the delete unless it was never sent, then try to send. */
export async function deleteEstimate(id: string): Promise<void> {
  const pending = await pendingCreate(id);
  await db.transaction('rw', db.estimates, db.outbox, async () => {
    await db.estimates.delete(id);
    const others = await db.outbox.where('estimateId').equals(id).toArray();
    await db.outbox.bulkDelete(others.map((o) => o.id));
    if (!pending) {
      await enqueue({
        id: crypto.randomUUID(),
        kind: 'delete',
        estimateId: id,
        createdAt: nowIso(),
        attempts: 0,
      });
    }
  });
  await drainOutbox();
}
